import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GraduationCap, Users, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { type Role } from '@/services/UserManagmentServices';
import { ROLE_ONBOARDING_ROUTE } from '@/lib/roleDisplay';
import logoLockup from '@/assets/logo-lockup-color.svg';
import { cn } from '@/lib/utils';
import { usePageTitle } from '@/hooks/usePageTitle';

const roles = [
  {
    role: 'TEACHER' as Role,
    icon: GraduationCap,
    label: "I'm a Teacher",
    tagline: 'Share your expertise',
    description: 'Build your teaching profile, create offerings, and grow a student community around your skills.',
    perks: ['Set your own schedule & rates', 'Manage bookings & inquiries', 'Track earnings & reviews'],
    gradient: 'from-[hsl(210,60%,18%)] to-[hsl(210,60%,30%)]',
    iconBg: 'bg-[hsl(210,60%,18%)]',
    ring: 'ring-[hsl(210,60%,18%)]',
    badge: 'bg-[hsl(210,60%,95%)] text-[hsl(210,60%,18%)]',
    cta: 'Continue as Teacher',
  },
  {
    role: 'STUDENT' as Role,
    icon: Users,
    label: "I'm a Student",
    tagline: 'Accelerate your growth',
    description: 'Discover expert teachers, book one-on-one or group sessions, and track your learning journey.',
    perks: ['Browse verified teachers', 'Book sessions instantly', 'Free trial sessions available'],
    gradient: 'from-[hsl(165,55%,30%)] to-[hsl(165,55%,42%)]',
    iconBg: 'bg-[hsl(165,55%,38%)]',
    ring: 'ring-[hsl(165,55%,38%)]',
    badge: 'bg-[hsl(165,55%,95%)] text-[hsl(165,55%,32%)]',
    cta: 'Continue as Student',
  },
] as const;

export default function SelectRole() {
  usePageTitle('Choose Your Role | BookMyCoaching');
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Role | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleContinue = () => {
    if (!selected) return;
    // Do NOT create the role here. Route the user into the selected role's
    // onboarding; the role is created only when they submit their first
    // required piece of info (goal profile for students, the basic-info
    // section for teachers). Abandoning onboarding leaves no empty account.
    // Any pendingIntent saved by OAuthCallback stays in localStorage and is
    // consumed by GoalOnboardingPage once the student finishes.
    setSubmitting(true);
    navigate(ROLE_ONBOARDING_ROUTE[selected], { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-12">
      {/* Logo */}
      <Link to="/" className="mb-10 flex justify-center group">
        <img
          src={logoLockup}
          alt="BookMyCoaching"
          className="h-10 md:h-14 w-auto max-w-full group-hover:opacity-90 transition-opacity"
        />
      </Link>

      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/8 text-primary text-xs font-semibold mb-5 border border-primary/15">
            <Sparkles className="w-3.5 h-3.5" />
            One-time setup
          </div>
          <h1 className="font-sans text-3xl sm:text-4xl font-bold text-foreground mb-3 leading-snug">
            How will you use BookMyCoaching?
          </h1>
          <p className="text-muted-foreground text-base max-w-sm mx-auto">
            Choose your role to personalise your experience. You can always switch later.
          </p>
        </div>

        {/* Role cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {roles.map(({ role, icon: Icon, label, tagline, description, perks, gradient, iconBg, ring, badge, cta }) => {
            const isSelected = selected === role;
            return (
              <button
                key={role}
                type="button"
                disabled={submitting}
                onClick={() => setSelected(role)}
                className={cn(
                  'relative text-left rounded-2xl border-2 p-6 transition-all duration-200 focus:outline-none group',
                  isSelected
                    ? `border-transparent ring-2 ${ring} shadow-lg scale-[1.02]`
                    : 'border-border hover:border-muted-foreground/30 hover:shadow-md'
                )}
              >
                {/* Selected checkmark */}
                <div className={cn(
                  'absolute top-4 right-4 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200',
                  isSelected ? 'bg-[hsl(165,55%,38%)] scale-100 opacity-100' : 'scale-75 opacity-0'
                )}>
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>

                {/* Icon */}
                <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center mb-4', iconBg)}>
                  <Icon className="w-6 h-6 text-white" />
                </div>

                {/* Badge */}
                <span className={cn('inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full mb-3', badge)}>
                  {tagline}
                </span>

                <h2 className="font-sans text-xl font-bold text-foreground mb-2">{label}</h2>
                <p className="text-sm text-muted-foreground mb-5 leading-relaxed">{description}</p>

                <ul className="space-y-2">
                  {perks.map((perk) => (
                    <li key={perk} className="flex items-center gap-2 text-sm text-foreground/80">
                      <CheckCircle className="w-4 h-4 flex-shrink-0 text-[hsl(165,55%,38%)]" />
                      {perk}
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        {/* CTA */}
        <button
          type="button"
          disabled={!selected || submitting}
          onClick={handleContinue}
          className={cn(
            'w-full flex items-center justify-center gap-2 py-3.5 rounded-full text-white font-semibold text-base transition-all duration-200',
            selected && !submitting
              ? 'bg-gradient-to-r from-[hsl(210,60%,18%)] to-[hsl(210,60%,30%)] hover:opacity-90 shadow-lg'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          )}
        >
          {submitting ? (
            <>
              <div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
              Saving…
            </>
          ) : (
            <>
              {selected ? roles.find(r => r.role === selected)?.cta : 'Select a role to continue'}
              {selected && <ArrowRight className="w-4 h-4" />}
            </>
          )}
        </button>

        <p className="mt-5 text-xs text-center text-muted-foreground">
          By continuing, you agree to our{' '}
          <Link to="/terms" className="underline hover:text-foreground transition-colors">Terms</Link>
          {' '}and{' '}
          <Link to="/privacy" className="underline hover:text-foreground transition-colors">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
